import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  Inject
} from '@angular/core';
import {  Region } from '@biletix/core/common';
import { I18nService } from '@biletix/core/services/i18n.service';
import { RegionService } from '@biletix/core/services/region.service';
import { TranslateService } from '@ngx-translate/core'; 
import { APP_BASE_HREF } from "@angular/common";
import { AbstractionComponent } from '@biletix/core/common/abstraction-component';

@Component({
  selector: 'btx-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: false
})
export class CalendarComponent extends AbstractionComponent implements OnInit {
  @ViewChild('cartPopdropHolder') cartPopdropHolder: ElementRef | undefined;
  @Input() data: any[] = [];
  @Input() eventName: string = "";
  @Input() type: string = '';
  withTheSameDate: any[] = [];
  regions: Region[] = [];
  currentRegion: string = '';
  currentLang: string = '';
  daysOfWeek: string[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  daysOfWeekLong: string[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  private months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  monthDays: { day: number; dayOfWeek: number; date?: any }[] = [];
  currentMonth: string = '';
  currentYear: number = 0;
  dialogStatus: boolean = false;
  isSmallScreen: boolean = false;
  isMenuOpen: boolean = false;
  menuDay: any = null;

  timeFormat = 'HH:mm';
  dayFormat = 'd';
  monthFormat = 'LLLL';
  constructor(
    private translateService: TranslateService,
    private breakpointObserver: BreakpointObserver,
    public region: RegionService,
    @Inject(APP_BASE_HREF) private baseHref: string,
    public i18n: I18nService
  ) {
    super();
    this.regions = this.region.getRegions();
    this.currentRegion = this.region.currentRegion.name;
    this.currentLang = this.i18n.currentLocale.lang;
  }
  ngOnInit(): void {
    this.data = this.data.filter(e => e.status !== 's00_closed');
    const mobileQuery = [Breakpoints.XSmall, Breakpoints.Small];
    this.isSmallScreen = this.breakpointObserver.isMatched(mobileQuery);
    this.breakpointObserver
      .observe(mobileQuery).pipe(this.takeUntilDestroy())
      .subscribe((result) => {
        this.isSmallScreen = result.matches;
      });

    let today = new Date();
    if (this.data && this.data.length > 0) {
      today = new Date(this.data[0].performanceDate);
    }
    this.generateCalendar(today.getFullYear(), today.getMonth());
    this.filterData();
  }
  hasOnSaleEvent(day: any): boolean {
    return this.data.some(
      (event) =>
        this.isSameDay(event, day.date) &&
        event.status == 's01_onsale'
    );
  }

  generateHref(eventCode?: string, performanceCode?: string, region?: string, lang?: string, keyUrlText?: string): string {
    if (!eventCode || !region || !lang) {
        return ''; 
    }
    if (keyUrlText) {
        return `${this.baseHref}performance/${eventCode}/${performanceCode}/${region}/${lang}/${keyUrlText}`;
    }
    return `${this.baseHref}performance/${eventCode}/${performanceCode}/${region}/${lang}`;
  }

  generateCalendar(year: number, month: number): void {
    this.monthDays = [];

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    this.currentMonth = firstDay.toLocaleString('en-US', { month: 'long' });
    this.currentYear = year;

    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1; // Convert Sunday (0) to 6, and shift other days back by one

    for (let i = 0; i < startingDayOfWeek; i++) {
      const day = new Date(year, month, -i);
      this.monthDays.unshift({
        day: day.getDate(),
        dayOfWeek: (7 + startingDayOfWeek - i) % 7,
        date: day,
      });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dayOfWeek = (startingDayOfWeek + i - 1) % 7;
      const day = new Date(year, month, i);
      this.monthDays.push({ day: i, dayOfWeek, date: day });
    }

    const remainingDays = 42 - this.monthDays.length;
    for (let i = 1; i <= remainingDays; i++) {
      const day = new Date(year, month + 1, i);
      this.monthDays.push({
        day: day.getDate(),
        dayOfWeek: (startingDayOfWeek + lastDay.getDate() + i - 1) % 7,
        date: day,
      });
    }

  }
  goToPreviousMonth(): void {
    const previousMonth = this.currentMonthIndex() - 1;
    const newDate = new Date(this.currentYear, previousMonth, 1);
    this.generateCalendar(newDate.getFullYear(), newDate.getMonth());
  }
  goToNextMonth(): void {
    const nextMonth = this.currentMonthIndex() + 1;
    const newDate = new Date(this.currentYear, nextMonth, 1);
    this.generateCalendar(newDate.getFullYear(), newDate.getMonth());
  }
  private currentMonthIndex(): number {
    return this.months.indexOf(this.currentMonth);
  }
  toggleMenu(day?: any) {
    this.isMenuOpen = !this.isMenuOpen;
    this.menuDay = day;
  }

  filterData() {
    const dateMap: Map<string, any[]> = new Map();

    this.data.forEach((event) => {
      const dateString = (event.performanceDate?event.performanceDate:event.firstPerformanceDate);
      if (!dateMap.has(dateString)) {
        dateMap.set(dateString, []);
      }

      const eventsForDate: any = dateMap.get(dateString);
      if (eventsForDate.length < 1) {
        eventsForDate.push(event);
      }
    });

    this.withTheSameDate = Array.from(dateMap.values()).reduce(
      (acc, val) => acc.concat(val),
      []
    );
  }

  getDateFromEpoch(epoch:number) {
    return new Date(epoch);
  }

  getSameDateEvents(date:any) {
    return this.data.filter(
      (event) => {
        return this.isSameDay(event, date);
      }

    );
  }
  getFirstEventByStatus(date:any): any|undefined {
    const events = this.getSameDateEvents(date);
    let resultEvent: any|undefined;
    if (events && events.length > 0) {
      const priorityList = ['s01_onsale', 's03_soon', 's05_postponed', 's02_soldout', 's12_checkbacklater',
        's04_canceled','s00_closed','s06_boxoffice','s16_rescheduled','s17_venue_changed','s14_other_channels','s13_old_over',]
      priorityList.forEach(priorityStatus => {
        if (resultEvent) return;
        events.forEach(e => {
          if ( e.status === priorityStatus) {
            resultEvent = e;
            return;
          }
        });
      });
      return resultEvent;
    }
  }

  isSameDay(event:any, date:Date) {
    const lastDay = new Date(date);
    lastDay.setDate(date.getDate() + 1);
    return lastDay > new Date(event.performanceDate?event.performanceDate:event.firstPerformanceDate)
    && date < new Date(event.performanceDate?event.performanceDate:event.firstPerformanceDate)
  }

  getSameMonthEventCount(month:string) {
    const first = this.monthDays[0].date;
    const last = this.monthDays[this.monthDays.length - 1].date;
    return this.data.filter((event) => new Date(event.performanceDate) >= first && new Date(event.performanceDate)<= last).length;
  }

  getEventText(date: any) {
    const events = this.getSameDateEvents(date);
    const keyText =  this.type === 'venue'?'eventName':'venueName';
    if (events.length <= 0) {
      return '';
    } else if (events.length === 1  || events.every(event => event[keyText] === events[0][keyText])) {
      return events[0][keyText];
    } else {
      return "performance.labels.multipleEvents";
    }
  }

  getEventName(date: any): string {
    const events = this.getSameDateEvents(date);
    if (!events || events.length <= 0) {
      return '';
    } else if (events.length === 1 || events.every(event => event.eventName === events[0].eventName)) {
      return events[0]?.eventName || this.eventName || '';
    } else {
      return "performance.labels.multipleEvents";
    }
  }

  getVenueText(date: any): string {
    const events = this.getSameDateEvents(date);
    if (!events || events.length <= 0) {
      return '';
    } else if (events.length === 1 || events.every(event => event.venueName === events[0].venueName)) {
      return events[0]?.venueName || '';
    } else {
      return this.currentLang === 'en' ? 'Multiple Venues...' : 'Çeşitli mekanlar...';
    }
  }

  getEventVenueName(date: any): string {
    const events = this.getSameDateEvents(date);

if (events.length === 1) {
  const venueNameParts = events[0].venueName.split(' ');
  return `${venueNameParts[0]} ${venueNameParts[1]}...`;
} else if (events.length > 1) {
  const venueName = events[0].venueName;
  const allSameVenue = events.every(event => event.venueName === venueName);

  if (allSameVenue) {
    const venueNameParts = venueName.split(' ');
    return `${venueNameParts[0]} ${venueNameParts[1]}...`;
  } else {
    return this.currentLang=== 'en' ? 'Multiple Venues...' : 'Çeşitli mekanlar...';
  }
} else {
  return '';
}
}
}
